import { Component, Input, AfterViewInit, OnDestroy, ViewChildren, QueryList, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { AppIconComponent } from "../../../../common/components/icon/icon.component";
import { DatepickerInputComponent } from '../../../../common/components/datepicker-input/datepicker-input.component';
import { Subscription } from 'rxjs';
import { KG_TO_LB, computeBmi } from '../../../../common/utils/bmi-validators';


type PopoverCtor = typeof import('bootstrap/js/dist/popover.js').default;
type PopoverInstance = import('bootstrap/js/dist/popover.js').default;

@Component({
    selector: 'app-checkout-insured-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, NgxMaskDirective, AppIconComponent, DatepickerInputComponent],
    templateUrl: './checkout-insured-form.component.html',
    styleUrls: ['./checkout-insured-form.component.scss']
})
export class CheckoutInsuredFormComponent implements AfterViewInit, OnDestroy, OnChanges {
    @Input({ required: true }) form!: FormGroup;
    @Input() documentMask: string = '000-0000000-0';
    @Input() readonlyBirthDate: boolean = false;
    @Input() readonlyGender: boolean = false;
    /** Latest passport expiration date allowed (ISO). */
    @Input() passportExpiryMax: string | null = null;

    get isPassport(): boolean {
        return this.form?.get('documentType')?.value === 'Passport';
    }

    get passportExpiryMaxDisplay(): string {
        const m = (this.passportExpiryMax ?? '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
        return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
    }

    get today(): string {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }


    /** The person's current BMI, or null until weight and height are filled in. */
    get bmiValue(): number | null {
        const f = this.form;
        return computeBmi(f?.get('weightLb')?.value, f?.get('heightFeet')?.value, f?.get('heightInches')?.value);
    }

    /** Set by the group-level `bmiValidator` on the parent form. */
    get bmiExceedsMax(): boolean {
        return !!this.form?.hasError('bmiExceeded');
    }

    get idLabel(): string {
        return 'No. de documento';
    }

    @ViewChildren('infoButton') infoButtons!: QueryList<ElementRef<HTMLElement>>;

    private popoverCtor?: PopoverCtor;
    private readonly popovers = new Map<HTMLElement, PopoverInstance>();
    private heightSub?: Subscription;
    private weightSub?: Subscription;

    ngAfterViewInit(): void {
        this.ensurePopovers();
        this.bindHeightSync();
        this.bindWeightSync();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['form']) {
            this.bindHeightSync();
            this.bindWeightSync();
        }
    }

    ngOnDestroy(): void {
        this.detachPopovers();
        this.heightSub?.unsubscribe();
        this.weightSub?.unsubscribe();
    }

    onInfoClick(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        const button = event.currentTarget as HTMLButtonElement | null;
        button?.focus();
    }

    private async ensurePopovers(): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }

        const buttons = this.infoButtons ?? [];
        const elements = new Set<HTMLElement>();
        buttons.forEach((buttonRef) => elements.add(buttonRef.nativeElement));

        if (!elements.size) {
            this.detachPopovers();
            return;
        }

        if (!this.popoverCtor) {
            const { default: Popover } = await import('bootstrap/js/dist/popover.js');
            this.popoverCtor = Popover;
        }

        this.popovers.forEach((popover, element) => {
            if (!elements.has(element)) {
                popover.dispose();
                this.popovers.delete(element);
            }
        });

        elements.forEach((element) => {
            if (this.popovers.has(element) || !this.popoverCtor) {
                return;
            }

            const title = element.dataset['infoTitle'] ?? '';
            const content = element.dataset['infoContent'] ?? '';

            const popover = new this.popoverCtor(element, {
                trigger: 'focus',
                title,
                content,
                container: 'body',
                html: false,
                customClass: 'custom-popover',
            });

            this.popovers.set(element, popover);
        });
    }

    private detachPopovers(): void {
        this.popovers.forEach((popover) => popover.dispose());
        this.popovers.clear();
    }

    private bindHeightSync(): void {
        this.heightSub?.unsubscribe();
        if (!this.form) return;

        const feetCtrl = this.form.get('heightFeet');
        const inchesCtrl = this.form.get('heightInches');
        const cmCtrl = this.form.get('heightCm');
        if (!feetCtrl || !inchesCtrl || !cmCtrl) return;

        const sync = () => {
            const feet = Number(feetCtrl.value);
            const inches = Number(inchesCtrl.value);
            if (!Number.isFinite(feet) || !Number.isFinite(inches)) {
                cmCtrl.setValue(null, { emitEvent: false });
                return;
            }
            const totalInches = feet * 12 + inches;
            if (totalInches <= 0) {
                cmCtrl.setValue(null, { emitEvent: false });
                return;
            }
            const cm = +(totalInches * 2.54).toFixed(2);
            cmCtrl.setValue(cm, { emitEvent: false });
        };

        this.heightSub = new Subscription();
        this.heightSub.add(feetCtrl.valueChanges.subscribe(sync));
        this.heightSub.add(inchesCtrl.valueChanges.subscribe(sync));
        sync();
    }

    /**
     * Keeps `weightLb` (canonical, always pounds - used for validation and submission
     * everywhere else) in sync with the unit-aware `weightDisplay`/`weightUnit` pair the
     * user actually interacts with. Switching units converts the displayed number so it
     * still represents the same physical weight, rather than reinterpreting it.
     */
    private bindWeightSync(): void {
        this.weightSub?.unsubscribe();
        if (!this.form) return;

        const displayCtrl = this.form.get('weightDisplay');
        const unitCtrl = this.form.get('weightUnit');
        const lbCtrl = this.form.get('weightLb');
        if (!displayCtrl || !unitCtrl || !lbCtrl) return;

        const toLb = (value: number, unit: string) =>
            unit === 'kg' ? value * KG_TO_LB : value;
        const fromLb = (lb: number, unit: string) =>
            unit === 'kg' ? lb / KG_TO_LB : lb;

        const syncLbFromDisplay = () => {
            const display = Number(displayCtrl.value);
            const unit = unitCtrl.value as string;
            if (!Number.isFinite(display) || display <= 0) {
                lbCtrl.setValue(null, { emitEvent: false });
                return;
            }
            lbCtrl.setValue(+toLb(display, unit).toFixed(2), { emitEvent: false });
        };

        this.weightSub = new Subscription();
        this.weightSub.add(displayCtrl.valueChanges.subscribe(syncLbFromDisplay));

        let previousUnit = (unitCtrl.value as string) ?? 'lb';
        this.weightSub.add(unitCtrl.valueChanges.subscribe((newUnit: string) => {
            const display = Number(displayCtrl.value);
            if (Number.isFinite(display) && display > 0) {
                const lb = toLb(display, previousUnit);
                displayCtrl.setValue(+fromLb(lb, newUnit).toFixed(1), { emitEvent: false });
            }
            previousUnit = newUnit;
            syncLbFromDisplay();
        }));

        syncLbFromDisplay();
    }
}
